import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api, SalesHistoryRow, SalesHistoryStats } from "../api";
import { formatVND } from "../utils";
import { GlassPanel, KpiCard, DataTable, SkeletonTable, ErrorState, EmptyState } from "../components";
import type { Column } from "../components";
import { useResyncListener } from "../hooks";

function todayVN(): string {
  const d = new Date(Date.now() + 7 * 3600 * 1000);
  return d.toISOString().slice(0, 10);
}
function offsetDay(days: number): string {
  const d = new Date(Date.now() + 7 * 3600 * 1000 - days * 24 * 3600 * 1000);
  return d.toISOString().slice(0, 10);
}
function toDMY(iso: string): string {
  const [y, m, d] = iso.split("-");
  return `${d}/${m}/${y}`;
}
/** sold_at ISO → "dd/mm/yyyy HH:mm" (local VN); fallback sale_date. */
function formatSaleWhen(r: SalesHistoryRow): string {
  if (r.sold_at) {
    const d = new Date(r.sold_at);
    if (!Number.isNaN(d.getTime())) {
      const date = d.toLocaleDateString("vi-VN", { day: "2-digit", month: "2-digit", year: "numeric" });
      const time = d.toLocaleTimeString("vi-VN", { hour: "2-digit", minute: "2-digit" });
      return `${date} ${time}`;
    }
  }
  return toDMY(r.sale_date);
}

export default function SalesHistoryPage() {
  const [searchParams] = useSearchParams();
  const initFrom = searchParams.get("from") || offsetDay(90);
  const initTo = searchParams.get("to") || todayVN();
  const initQ = searchParams.get("q") || "";
  const [rows, setRows] = useState<SalesHistoryRow[]>([]);
  const [total, setTotal] = useState(0);
  const [stats, setStats] = useState<SalesHistoryStats | null>(null);
  const [cats, setCats] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [from, setFrom] = useState(initFrom);
  const [to, setTo] = useState(initTo);
  const [q, setQ] = useState(initQ);
  const [category, setCategory] = useState("");
  const defaultFrom = offsetDay(90);
  const defaultTo = todayVN();
  const advancedFilterActive = from !== defaultFrom || to !== defaultTo || category !== "";
  const [showAdvancedFilter, setShowAdvancedFilter] = useState(
    initFrom !== defaultFrom || initTo !== defaultTo
  );
  const [offset, setOffset] = useState(0);
  const limit = 50;
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // remount uncontrolled search input when deep-link q changes
  const [searchInputKey, setSearchInputKey] = useState(0);

  useEffect(() => {
    api.salesHistoryCategories().then((r) => setCats(r.categories)).catch(() => {});
  }, []);

  // Deep-link /sales-history?q=&from=&to= (từ Dashboard)
  useEffect(() => {
    const f = searchParams.get("from");
    const t = searchParams.get("to");
    const qq = searchParams.get("q");
    if (f) setFrom(f);
    if (t) setTo(t);
    if (qq != null) {
      setQ(qq);
      setSearchInputKey((k) => k + 1);
    }
    if ((f && f !== defaultFrom) || (t && t !== defaultTo)) setShowAdvancedFilter(true);
    setOffset(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- chỉ theo URL
  }, [searchParams]);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setError(null);
    api
      .salesHistory({ from, to, q: q || undefined, category: category || undefined, limit, offset })
      .then((r) => {
        if (!alive) return;
        setRows(r.rows);
        setTotal(r.total);
      })
      .catch((e) => alive && setError(e.message))
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [from, to, q, category, offset]);

  useEffect(() => {
    let alive = true;
    api.salesHistoryStats(from, to, q || undefined, category || undefined)
      .then((r) => alive && setStats(r))
      .catch(() => {});
    return () => { alive = false; };
  }, [from, to, q, category]);

  // Listen for global sync
  useResyncListener(() => {
    api.salesHistory({ from, to, q: q || undefined, category: category || undefined, limit, offset })
      .then((r) => { setRows(r.rows); setTotal(r.total); })
      .catch(() => {});
    api.salesHistoryStats(from, to, q || undefined, category || undefined)
      .then((r) => setStats(r))
      .catch(() => {});
  });

  const onSearch = (val: string) => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setOffset(0);
      setQ(val.trim());
    }, 300);
  };

  const columns: Column<SalesHistoryRow>[] = useMemo(
    () => [
      {
        key: "sale_date",
        header: "Ngày",
        sortable: true,
        render: (r) => formatSaleWhen(r),
      },
      {
        key: "receipt_number",
        header: "Đơn",
        render: (r) =>
          r.receipt_number ? (
            <Link to={`/orders/${r.receipt_number}`}>#{r.receipt_number}</Link>
          ) : (
            "—"
          ),
      },
      {
        key: "item_name",
        header: "Sản phẩm",
        render: (r) => (
          <div>
            <div style={{ fontWeight: 600 }}>{r.item_name}</div>
            {r.sku && <div className="meta" style={{ marginTop: 2 }}>SKU: {r.sku}</div>}
          </div>
        ),
      },
      {
        key: "sku",
        header: "SKU",
        render: (r) => r.sku || "—",
      },
      {
        key: "qty",
        header: "SL",
        render: (r) => r.qty,
      },
      {
        key: "unit_price",
        header: "Đơn giá",
        render: (r) => formatVND(r.unit_price),
      },
      {
        key: "revenue",
        header: "Doanh thu",
        sortable: true,
        align: "right",
        render: (r) => <span style={{ fontWeight: 600 }}>{formatVND(r.revenue)}</span>,
      },
      {
        key: "cost",
        header: "Giá vốn",
        render: (r) => formatVND(r.cost),
      },
      {
        key: "profit",
        header: "Lãi",
        render: (r) => {
          const p = r.revenue - r.cost;
          const color = p > 0 ? "var(--accent-green)" : p < 0 ? "var(--accent-red)" : "var(--text-tertiary)";
          return <span style={{ fontWeight: 700, color }}>{formatVND(p)}</span>;
        },
      },
      {
        key: "category",
        header: "Danh mục",
        render: (r) => r.category || "—",
      },
    ],
    []
  );

  const totalPages = Math.max(1, Math.ceil(total / limit));
  const page = Math.floor(offset / limit) + 1;

  return (
    <>
      <div className="kpi-grid" style={{ marginBottom: "var(--sp-lg)" }}>
        <KpiCard label="Doanh thu" value={formatVND(stats?.total_revenue ?? 0)} />
        <KpiCard label="Giá vốn" value={formatVND(stats?.total_cost ?? 0)} />
        <KpiCard label="Lãi gộp" value={formatVND(stats?.gross_profit ?? 0)} note={""} dotColor={stats && stats.gross_profit >= 0 ? "ok" : "bad"} />
        <KpiCard label="Số mặt hàng" value={stats?.distinct_items ?? 0} />
      </div>

      <GlassPanel title="Bộ lọc">
        <div className="filter-bar">
          <div className="search" style={{ flex: 1, minWidth: 160 }}>
            <span className="material-symbols-outlined">search</span>
            <input
              key={searchInputKey}
              type="text"
              placeholder="Sản phẩm, SKU…"
              defaultValue={q}
              onChange={(e) => onSearch(e.target.value)}
            />
          </div>
          <button
            className={`btn-icon ${advancedFilterActive ? "active" : ""}`}
            onClick={() => setShowAdvancedFilter((v) => !v)}
            title="Bộ lọc"
            aria-label="Bộ lọc"
            style={advancedFilterActive ? { background: "var(--accent-blue)", color: "#fff" } : undefined}
          >
            <span className="material-symbols-outlined" style={{ fontSize: 18 }}>tune</span>
          </button>
        </div>

        {showAdvancedFilter && (
          <div className="filter-advanced">
            <div className="filter-field">
              <label>Từ ngày</label>
              <input type="date" value={from} onChange={(e) => { setFrom(e.target.value); setOffset(0); }} />
            </div>
            <div className="filter-field">
              <label>Đến ngày</label>
              <input type="date" value={to} onChange={(e) => { setTo(e.target.value); setOffset(0); }} />
            </div>
            <div className="filter-field">
              <label>Danh mục</label>
              <select value={category} onChange={(e) => { setCategory(e.target.value); setOffset(0); }}>
                <option value="">Tất cả</option>
                {cats.map((c) => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
            </div>
            {advancedFilterActive && (
              <button
                className="btn btn-secondary"
                onClick={() => {
                  setFrom(defaultFrom);
                  setTo(defaultTo);
                  setCategory("");
                  setOffset(0);
                }}
              >
                Xoá lọc
              </button>
            )}
          </div>
        )}
      </GlassPanel>

      <GlassPanel
        title="Lịch sử bán"
        badge={`${total} dòng`}
        badgeVariant="info"
      >
        {loading ? (
          <SkeletonTable rows={8} cols={6} />
        ) : error ? (
          <ErrorState error={error} onRetry={() => { setLoading(true); setError(null); api.salesHistory({ from, to, q: q || undefined, category: category || undefined, limit, offset }).then((r) => { setRows(r.rows); setTotal(r.total); }).catch((e) => setError(e.message)).finally(() => setLoading(false)); }} />
        ) : rows.length === 0 ? (
          <EmptyState icon="bar_chart" title="Không có dữ liệu" description="Không có dữ liệu bán hàng trong khoảng này." />
        ) : (
          <>
            <DataTable
              columns={columns}
              rows={rows}
              rowKey={(r) => String(r.id)}
              emptyMessage="Không có dữ liệu"
              pageSize={50}
              defaultSort={{ key: "sale_date", dir: "desc" }}
            />
          </>
        )}
      </GlassPanel>
    </>
  );
}
